export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export function formatBalances(
  balances: Record<string, number> | undefined,
  filterCurrency?: string
): string {
  if (!balances || Object.keys(balances).length === 0) {
    return 'No balances found.';
  }

  const entries = Object.entries(balances).filter(([, v]) => v !== 0 || !filterCurrency);
  const filtered = filterCurrency
    ? entries.filter(([c]) => c.toUpperCase() === filterCurrency.toUpperCase())
    : entries;

  if (filtered.length === 0) {
    if (filterCurrency) {
      const value = balances[filterCurrency] ?? 0;
      return `${filterCurrency}: ${formatMoney(value, filterCurrency)}`;
    }
    return 'No non-zero balances.';
  }

  return filtered
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, amount]) => `• ${currency}: ${formatMoney(amount, currency)}`)
    .join('\n');
}

export function formatTransactions(
  transactions: Array<{
    type?: string;
    status?: string;
    amount?: number;
    currency?: string;
    description?: string;
    createdAt?: string;
    counterpartyName?: string;
  }>,
  max = 10
): string {
  if (!transactions.length) {
    return 'No transactions found.';
  }

  return transactions
    .slice(0, max)
    .map((tx, i) => {
      const date = tx.createdAt
        ? new Date(tx.createdAt).toLocaleDateString()
        : '—';
      const amount =
        tx.amount != null && tx.currency
          ? formatMoney(tx.amount, tx.currency)
          : '—';
      const who = tx.counterpartyName ? ` · ${tx.counterpartyName}` : '';
      const desc = tx.description ? ` — ${tx.description}` : '';
      return `${i + 1}. ${date} · ${tx.type ?? 'tx'} · ${tx.status ?? '—'} · ${amount}${who}${desc}`;
    })
    .join('\n');
}

export function formatContacts(
  contacts: Array<{
    name?: string;
    username?: string;
    email?: string;
    role?: string;
  }>,
  max = 15
): string {
  if (!contacts.length) return 'No contacts found.';
  return contacts
    .slice(0, max)
    .map((c, i) => {
      const name = c.name || c.username || c.email || 'Unknown';
      const role = c.role ? ` (${c.role})` : '';
      const handle = c.username ? ` @${c.username}` : '';
      return `${i + 1}. ${name}${handle}${role}`;
    })
    .join('\n');
}

export function formatPaymentMethods(
  methods: Array<{
    type?: string;
    last4?: string;
    brand?: string;
    bankName?: string;
    isDefault?: boolean;
  }>
): string {
  if (!methods.length) return 'No payment methods on file.';
  return methods
    .map((m, i) => {
      const def = m.isDefault ? ' ★ default' : '';
      if (m.type === 'card' || m.brand) {
        return `${i + 1}. ${m.brand ?? 'Card'} •••• ${m.last4 ?? '????'}${def}`;
      }
      if (m.bankName || m.type === 'bank') {
        return `${i + 1}. ${m.bankName ?? 'Bank'} •••• ${m.last4 ?? '????'}${def}`;
      }
      return `${i + 1}. ${m.type ?? 'method'}${def}`;
    })
    .join('\n');
}

export function formatMerchants(
  merchants: Array<{ name?: string; description?: string; category?: string }>,
  max = 10
): string {
  if (!merchants.length) return 'No merchants found.';
  return merchants
    .slice(0, max)
    .map((m, i) => {
      const cat = m.category ? ` [${m.category}]` : '';
      const desc = m.description ? ` — ${m.description.slice(0, 60)}` : '';
      return `${i + 1}. ${m.name ?? 'Merchant'}${cat}${desc}`;
    })
    .join('\n');
}

export function formatInbox(
  messages: Array<{
    subject?: string;
    merchantName?: string;
    preview?: string;
    isRead?: boolean;
    createdAt?: string;
  }>,
  max = 10
): string {
  if (!messages.length) return 'Inbox is empty.';
  return messages
    .slice(0, max)
    .map((m, i) => {
      const unread = m.isRead === false ? ' 🔵' : '';
      const from = m.merchantName ?? 'Merchant';
      const subject = m.subject ?? m.preview ?? '(no subject)';
      return `${i + 1}. ${from}: ${subject}${unread}`;
    })
    .join('\n');
}
